import { GetDashboardUpcomingSessionModel } from "@factory/model/query-model-dashboard";
import { useQyGetUpcomingSessions } from "@factory/query/dashboard.query";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { Button } from "primereact/button";
import { FaRegCalendarAlt } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

import {
  buildSessionFilterQuery,
  SessionFilterValues,
} from "../upcoming-session.util";
import UpcomingSessionTable from "../upcoming-session-table/upcoming-session-table";

// How many sessions the dashboard shows before "View All Schedules"
export const UPCOMING_SESSION_WIDGET_LIMIT = 5;

export interface UpcomingSessionWidgetProps extends SessionFilterValues {}

export function UpcomingSessionWidget({
  dateFilter,
  startDate,
  endDate,
  category,
}: UpcomingSessionWidgetProps) {
  const navigate = useNavigate();
  const { client } = useParams<{ client: string }>();

  const filter = category
    ? { filter: JSON.stringify({ category }) }
    : undefined;
  const { data, isLoading, isError, refetch } = useQyGetUpcomingSessions(
    "",
    UPCOMING_SESSION_WIDGET_LIMIT,
    0,
    undefined,
    filter,
    dateFilter,
    startDate,
    endDate,
  );

  const handleViewAll = () => {
    navigate(
      `/${client}/upcoming-sessions${buildSessionFilterQuery({
        dateFilter,
        startDate,
        endDate,
        category,
      })}`,
    );
  };

  const handleRowSelect = (row: GetDashboardUpcomingSessionModel) => {
    navigate(
      `/${client}/visit/${row.visit}?name=${encodeURIComponent(
        row.client_name,
      )}`,
    );
  };

  return (
    <section
      id="UpcomingSessionWidget"
      className="border rounded-lg bg-white overflow-hidden mb-6"
    >
      <header className="flex items-center justify-between gap-4 bg-blue-50 px-5 py-4">
        <div className="flex items-start gap-3">
          <FaRegCalendarAlt className="text-blue-600 text-2xl mt-1" />
          <div>
            <h4 className="text-gray-800 font-semibold">
              Upcoming Sessions / Schedule
            </h4>
            <p className="text-gray-500 text-sm">
              List of upcoming client sessions based on the selected date
              filter.
            </p>
          </div>
        </div>
        <Button
          label="View All Schedules"
          icon="pi pi-arrow-right"
          iconPos="right"
          size="small"
          outlined
          onClick={handleViewAll}
        />
      </header>

      {isLoading && (
        <div className="p-4">
          <SkeletonList count={UPCOMING_SESSION_WIDGET_LIMIT} />
        </div>
      )}
      {isError && !isLoading && (
        <div className="flex items-center justify-between gap-4 p-4">
          <span className="text-gray-600">
            Unable to load upcoming sessions.
          </span>
          <Button
            label="Retry"
            icon="pi pi-refresh"
            severity="secondary"
            size="small"
            outlined
            onClick={() => refetch()}
          />
        </div>
      )}
      {!isLoading && !isError && (
        <UpcomingSessionTable
          rows={data?.data ?? []}
          emptyMessage="No upcoming sessions for the selected dates."
          onRowSelect={handleRowSelect}
        />
      )}
    </section>
  );
}

export default UpcomingSessionWidget;
